import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import { toastMessage } from 'utils/messages'

import { company as apiCompany, apiContentText } from 'api'
import { msConverter } from 'utils'
import MessagesConfigViewUI from './MessagesConfigViewUI'

const MessagesConfigView = () => {
  const { companies: company, operatorAutoDisabled } = useSelector(state => state)
  const [configurations, setConfigurations] = useState({})
  const [messagesConfigData, setMessagesConfigData] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const isDisabledSave = !!configurations?.error?.message

  useEffect(() => {
    let isSubscribed = true

    if (isSubscribed) {
      setConfigurations({
        loading: true,
        error: null
      })

      apiCompany.configurations.getConfiguration(company._id)
        .then(({ data, status }) => {
          if (status === 200) setConfigurations(prevState => ({
            ...prevState,
            data: data.results
          }))
        })
        .catch(() => setConfigurations({
          error: {
            tag: '',
            message: I18n.t('dashboard_error_fetch_default')
          }
        }))
        .finally(() => setConfigurations(prevState => ({
          ...prevState,
          loading: false
        })))
    }

    return () => isSubscribed = false
  }, [])

  const onChangeInner = ({ tag, value }) => {
    setMessagesConfigData(prevState => ({
      ...prevState,
      [tag]: value
    }))
  }

  const getMsgData = ({ tag }) => ([
    tag,
    {
      _id: messagesConfigData[tag].id,
      message: messagesConfigData[tag].message
    },
    {
      time: msConverter({
        value: messagesConfigData[tag].timer.time,
        target: 'ms',
        origin: messagesConfigData[tag].timer.unit
      }),
      unit: messagesConfigData[tag].timer.unit
    }
  ])

  const getConfigurationsData = () => ({
    ...configurations.data,
    users: {
      isActive: messagesConfigData.users.activation.isActive,
      newState: messagesConfigData.users.singleSelector?.value || '',
      sessionTime: msConverter({
        value: getTimeValue({
          ...messagesConfigData.users.timer,
          defaultValues: [
            { unit: 'minutes', minTime: 10 }
          ],
          valueForZero: 1
        }),
        target: 'ms',
        origin: messagesConfigData.users.timer.unit
      }),
      statesToApply: messagesConfigData.users.multipleSelector?.map(state => state.value) || [],
      removeAssignedOperators: messagesConfigData.users.unassignOperators.removeAssignedOperators
    },
    operators: {
      isActive: messagesConfigData.operators.activation.isActive || true,
      sessionTime: msConverter({
        value: getTimeValue({
          ...messagesConfigData.operators.timer,
          defaultValues: [
            { unit: 'minutes', minTime: 10, maxTime: 60 },
            { unit: 'hours', minTime: 1, maxTime: 12 },
          ],
          valueForZero: 1
        }),
        target: 'ms',
        origin: messagesConfigData.operators.timer.unit
      })
    }
  })

  const handleSave = () => {
    setIsSaving(true)
    const configuration = getConfigurationsData()

    let configurationsPromise = null
    if (!configurations.data._id) {
      configurationsPromise = apiCompany.configurations.postConfiguration(company._id, configuration)
    } else {
      configurationsPromise = apiCompany.configurations.updateConfiguration(company._id, configuration)
    }

    const promises = [
      apiContentText.postRichText(...getMsgData({ tag: 'welcome' })),
      apiContentText.postRichText(...getMsgData({ tag: 'reWelcome' })),
      apiContentText.postRichText(...getMsgData({ tag: 'paymentOk' })),
      apiContentText.postRichText(...getMsgData({ tag: 'outOfOffice' })),
      apiContentText.postRichText(...getMsgData({ tag: 'clickToCall' })),
      configurationsPromise,
      apiCompany.operators.updateOperatorAutoDisabled({ ...operatorAutoDisabled })
    ]

    Promise.all(promises)
      .then(() => toastMessage(I18n.t('configurations_messages_success_saving')))
      .catch(() => toastMessage(I18n.t('configurations_messages_error_saving'), 'error'))
      .finally(() => setIsSaving(false))
  }

  return (
    <MessagesConfigViewUI
      company={company}
      configurations={configurations}
      onChange={onChangeInner}
      handleSave={handleSave}
      isSaving={isSaving}
      isDisabledSave={isDisabledSave}
    />
  )
}

export default MessagesConfigView

/**
 * Return right time value
 * @param {object} params
 * @param {string} params.unit
 * @param {number} params.time
 * @param {number} [params.valueForZero]
 * @param {[{unit: string, minTime: number, maxTime}]} [params.defaultValues]
 *
 * @returns {number}
 */
function getTimeValue(params) {
  const { unit, time, defaultValues = [], valueForZero = 0 } = params

  const defaultValue = defaultValues.find(e => e?.unit === unit)

  if (defaultValue?.minTime && time < defaultValue.minTime) return defaultValue.minTime
  if (defaultValue?.maxTime && time > defaultValue.maxTime) return defaultValue.maxTime

  return time || valueForZero
}